import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { User, MapPin, Navigation, Search, Loader2, Award, Bell, MessageCircle, Cpu, Users, Eye, EyeOff, Zap, Sprout, Trophy, Trash2, AlertTriangle, FileText, Shield, Mail, ChevronRight, Globe, CreditCard, Calendar, XCircle, RefreshCw, CheckCircle2, Crown } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, applyLanguage } from '@/i18n';
import { SUPPORT_EMAIL } from './Legal';
import EmojiPicker from '@/components/EmojiPicker';
import RegionPicker from '@/components/RegionPicker';
import { useTour } from '@/welcome/TourContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SettingsPage({ user }) {
  const { t } = useTranslation();
  const { start: startTour, openHelp } = useTour();
  const [localUser, setLocalUser] = useState(user);
  const [language, setLanguage] = useState(user?.language || 'en');
  const [aiCoach, setAiCoach] = useState(user?.ai_coach_enabled !== false);
  const [intelligence, setIntelligence] = useState(user?.intelligence_enabled !== false);
  const [communityEnabled, setCommunityEnabled] = useState(user?.community_enabled !== false);
  const [skillTree, setSkillTree] = useState(user?.skill_tree_enabled !== false);
  const [challenges, setChallenges] = useState(user?.challenges_enabled !== false);
  const [mainAction, setMainAction] = useState(user?.main_action || 'coach');
  const [upgradeMsg, setUpgradeMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiReenableSent, setAiReenableSent] = useState(false);
  const [aiReenableSending, setAiReenableSending] = useState(false);

  const requestAiReenable = async () => {
    setAiReenableSending(true);
    try {
      await axios.post(`${API}/support/ai-reenable-request`, {}, { withCredentials: true });
      setAiReenableSent(true);
    } catch { /* best-effort */ }
    finally { setAiReenableSending(false); }
  };

  const isPremium = !!(localUser?.subscription_active && ['premium', 'pro'].includes(localUser?.subscription_tier));

  const savePref = async (patch, revert) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/users/me`, patch, { withCredentials: true });
      const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setLocalUser(me.data);
    } catch (e) {
      if (revert) revert();
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const toggleCommunity = async (v) => {
    setUpgradeMsg('');
    if (v && !isPremium) { setUpgradeMsg(t('settings.communityPremiumNote')); return; }
    setCommunityEnabled(v);
    try { await savePref({ community_enabled: v }, () => setCommunityEnabled(!v)); }
    catch (e) { setCommunityEnabled(!v); if (e.response?.status === 402) setUpgradeMsg(t('settings.communityPremiumNote')); }
  };

  const changeMainAction = (v) => { const prev = mainAction; setMainAction(v); savePref({ main_action: v }, () => setMainAction(prev)).catch(() => {}); };

  const changeLanguage = (code) => {
    const prev = language;
    setLanguage(code);
    applyLanguage(code, true);
    savePref({ language: code }, () => { setLanguage(prev); applyLanguage(prev, true); }).catch(() => {});
  };

  const MAIN_ACTION_OPTIONS = [
    { value: 'coach', label: t('settings.maCoach') },
    { value: 'log_harvest', label: t('settings.maLogHarvest') },
    { value: 'scanner', label: t('settings.maScanner') },
    { value: 'new_planting', label: t('settings.maNewPlanting') },
    { value: 'community', label: t('settings.maCommunity') },
  ];

  return (
    <Layout user={localUser}>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('settings.title')}
          </h1>
          <p className="text-base text-[#5C665C] mt-2 leading-relaxed">
            {t('settings.manageProfile')}
          </p>
        </div>

        {/* Homegrown Premium — subscription management */}
        <SubscriptionSection />

        {/* Preferences — Homegrown Control Center */}
        <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="preferences-section">
          <h3 className="text-xl font-semibold text-[#2D241E] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('settings.preferences')}</h3>
          <p className="text-sm text-[#5C665C] mb-5">{t('settings.preferencesDesc')}</p>

          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-[#1A4331]/5" data-testid="language-setting">
            <div className="w-11 h-11 rounded-full bg-[#1A4331]/8 flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-[#1A4331]" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-[#2D241E]">{t('settings.language')}</p>
              <p className="text-sm text-[#5C665C]">{t('settings.languageDesc')}</p>
            </div>
            <select
              value={language}
              onChange={(e) => changeLanguage(e.target.value)}
              disabled={saving}
              data-testid="language-select"
              aria-label={t('settings.language')}
              className="rounded-xl border border-[#1A4331]/15 bg-[#FAF6EC] px-3 py-2 text-sm font-semibold text-[#2D241E] outline-none cursor-pointer"
            >
              {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.native}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-[#1A4331]/8 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-[#1A4331]" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-[#2D241E]">{t('settings.enableCommunity')}</p>
              <p className="text-sm text-[#5C665C]">{t('settings.enableCommunityDesc')}</p>
            </div>
            <button
              role="switch"
              aria-checked={communityEnabled}
              disabled={saving}
              onClick={() => toggleCommunity(!communityEnabled)}
              data-testid="toggle-community-enabled"
              className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${communityEnabled ? 'bg-[#6B9F4A]' : 'bg-[#D9E1CB]'}`}
            >
              <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${communityEnabled ? 'left-[22px]' : 'left-0.5'}`}></span>
            </button>
          </div>
          {upgradeMsg && (
            <div className="mt-3 flex items-center justify-between gap-3 bg-[#C9A85B]/12 border border-[#C9A85B]/30 rounded-xl px-4 py-2.5" data-testid="community-upgrade-note">
              <p className="text-sm text-[#1A4331]">{upgradeMsg}</p>
              <a href="/paywall" className="text-sm font-semibold text-[#C9A85B] whitespace-nowrap">{t('settings.upgradeArrow')}</a>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-[#1A4331]/5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-full bg-[#1A4331]/8 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-[#C9A85B]" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[#2D241E]">{t('settings.mainActionFocus')}</p>
                <p className="text-sm text-[#5C665C]">{t('settings.mainActionFocusDesc')}</p>
              </div>
            </div>
            <select
              value={mainAction}
              onChange={(e) => changeMainAction(e.target.value)}
              disabled={saving}
              data-testid="main-action-select"
              className="w-full px-4 py-3 bg-[#F0F2EB] border-transparent focus:border-[#8EAA97] focus:ring-2 focus:ring-[#8EAA97]/20 rounded-xl outline-none transition-all"
            >
              {MAIN_ACTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === 'community' && !communityEnabled}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Features */}
        <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="features-section">
          <h3 className="text-xl font-semibold text-[#2D241E] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('settings.features')}</h3>
          <p className="text-sm text-[#5C665C] mb-5">{t('settings.featuresDesc')}</p>
          {localUser?.ai_coach_forever_disabled === true ? (
            <div data-testid="ai-forever-note" className="bg-[#F0F2EB] rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-[#7A6B57] mt-0.5 shrink-0" />
                <p className="text-sm text-[#5C665C] leading-relaxed">
                  You chose to keep Homegrown AI-free during setup, so all AI features are permanently hidden. If you&apos;d like them turned back on, send our team a quick request below.
                </p>
              </div>
              {aiReenableSent ? (
                <p className="text-sm text-[#1A4331] font-medium mt-3 ml-8" data-testid="ai-reenable-sent">
                  Request sent — our team will be in touch to turn AI back on.
                </p>
              ) : (
                <button
                  onClick={requestAiReenable}
                  disabled={aiReenableSending}
                  data-testid="request-ai-reenable-button"
                  className="mt-3 ml-8 bg-[#1A4331] hover:bg-[#255C44] text-white text-sm font-semibold py-2.5 px-5 rounded-full transition-colors disabled:opacity-50"
                >
                  {aiReenableSending ? 'Sending…' : 'Contact support to turn AI back on'}
                </button>
              )}
            </div>
          ) : (
          <div className="space-y-4">
            <FeatureToggle
              icon={MessageCircle}
              title={t('settings.featAiCoach')}
              desc={t('settings.featAiCoachDesc')}
              checked={aiCoach}
              disabled={saving}
              testId="toggle-ai-coach"
              onChange={(v) => { setAiCoach(v); savePref({ ai_coach_enabled: v }, () => setAiCoach(!v)).catch(() => {}); }}
            />
            <FeatureToggle
              icon={Cpu}
              title={t('settings.featIntelligence')}
              desc={t('settings.featIntelligenceDesc')}
              checked={intelligence}
              disabled={saving}
              testId="toggle-intelligence"
              onChange={(v) => { setIntelligence(v); savePref({ intelligence_enabled: v }, () => setIntelligence(!v)).catch(() => {}); }}
            />
          </div>
          )}
          <div className="space-y-4 mt-4">
            <FeatureToggle
              icon={Sprout}
              title={t('settings.featSkillTree')}
              desc={t('settings.featSkillTreeDesc')}
              checked={skillTree}
              disabled={saving}
              testId="toggle-skill-tree"
              onChange={(v) => { setSkillTree(v); savePref({ skill_tree_enabled: v }, () => setSkillTree(!v)).catch(() => {}); }}
            />
            <FeatureToggle
              icon={Trophy}
              title={t('settings.featChallenges')}
              desc={t('settings.featChallengesDesc')}
              checked={challenges}
              disabled={saving}
              testId="toggle-challenges"
              onChange={(v) => { setChallenges(v); savePref({ challenges_enabled: v }, () => setChallenges(!v)).catch(() => {}); }}
            />
          </div>
        </div>

        {/* Help & Learning */}
        <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="help-section">
          <h3 className="text-xl font-semibold text-[#2D241E] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Help &amp; learning</h3>
          <p className="text-sm text-[#5C665C] mb-4">New here, or want a refresher?</p>
          <button
            onClick={openHelp}
            data-testid="open-help-centre"
            className="hg-press w-full flex items-center gap-3 bg-[#F0F2EB] hover:bg-[#E5EBDD] rounded-2xl px-4 py-3.5 transition-colors text-left mb-2.5"
          >
            <span className="w-10 h-10 rounded-xl bg-[#1A4331]/8 flex items-center justify-center flex-shrink-0">
              <Search className="w-[18px] h-[18px] text-[#1A4331]" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-semibold text-[14px] text-[#1A4331]">Help Centre</span>
              <span className="block text-[12px] text-[#5C665C]">Search short guides to every feature</span>
            </span>
            <ChevronRight className="w-4 h-4 text-[#5C665C]" />
          </button>
          <button
            onClick={() => startTour(true)}
            data-testid="open-replay-tutorial"
            className="hg-press w-full flex items-center gap-3 bg-[#F0F2EB] hover:bg-[#E5EBDD] rounded-2xl px-4 py-3.5 transition-colors text-left"
          >
            <span className="w-10 h-10 rounded-xl bg-[#1A4331]/8 flex items-center justify-center flex-shrink-0">
              <Navigation className="w-[18px] h-[18px] text-[#1A4331]" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-semibold text-[14px] text-[#1A4331]">Replay tutorial</span>
              <span className="block text-[12px] text-[#5C665C]">Take the guided welcome tour again</span>
            </span>
            <ChevronRight className="w-4 h-4 text-[#5C665C]" />
          </button>
        </div>

        {/* Community & Privacy */}
        <PrivacySection />

        {/* Profile */}
        <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-[#8EAA97]">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white text-2xl font-bold">
                  {user?.name?.[0]?.toUpperCase()}
                </div>
              )}
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {user?.name}
              </h2>
              <p className="text-[#5C665C]">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Location */}
        <LocationCard user={localUser} onSave={savePref} />

        {/* Skill Level */}
        <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="skill-section">
          <div className="flex items-center gap-3 mb-4">
            <Award className="w-5 h-5 text-[#1A4331]" />
            <h3 className="text-xl font-semibold text-[#2D241E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('settings.gardeningExperience')}
            </h3>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#2D241E] mb-2">
              {t('settings.skillLevel')}
            </label>
            <select
              defaultValue={user?.skill_level || 'beginner'}
              data-testid="skill-level-select"
              className="w-full px-4 py-3 bg-[#F0F2EB] border-transparent focus:border-[#8EAA97] focus:ring-2 focus:ring-[#8EAA97]/20 rounded-xl outline-none transition-all"
            >
              <option value="beginner">{t('settings.skillBeginner')}</option>
              <option value="intermediate">{t('settings.skillIntermediate')}</option>
              <option value="advanced">{t('settings.skillAdvanced')}</option>
            </select>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="notifications-section">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="w-5 h-5 text-[#1A4331]" />
            <h3 className="text-xl font-semibold text-[#2D241E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('settings.notifications')}
            </h3>
          </div>
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-[#2D241E]">{t('settings.notifDailyTasks')}</span>
              <input
                type="checkbox"
                defaultChecked
                data-testid="daily-tasks-toggle"
                className="w-12 h-6 rounded-full appearance-none bg-[#F0F2EB] checked:bg-[#1A4331] relative cursor-pointer transition-colors"
              />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-[#2D241E]">{t('settings.notifWeatherAlerts')}</span>
              <input
                type="checkbox"
                defaultChecked
                data-testid="weather-alerts-toggle"
                className="w-12 h-6 rounded-full appearance-none bg-[#F0F2EB] checked:bg-[#1A4331] relative cursor-pointer transition-colors"
              />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-[#2D241E]">{t('settings.notifHarvestReminders')}</span>
              <input
                type="checkbox"
                defaultChecked
                data-testid="harvest-reminders-toggle"
                className="w-12 h-6 rounded-full appearance-none bg-[#F0F2EB] checked:bg-[#1A4331] relative cursor-pointer transition-colors"
              />
            </label>
          </div>
        </div>

        {/* Save Button */}
        <button
          data-testid="save-settings-button"
          className="w-full bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-4 px-8 rounded-full transition-colors"
        >
          {t('settings.saveChanges')}
        </button>

        {/* Legal & Support */}
        <LegalSupportSection />

        {/* Danger Zone — Delete account */}
        <DeleteAccountSection />
      </div>
    </Layout>
  );
}

function SubscriptionSection() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const load = async () => {
    try { const r = await axios.get(`${API}/subscription/me`, { withCredentials: true }); setSub(r.data); }
    catch { /* best-effort */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId && params.get('upgrade') === 'success') pollStatus(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 12) return;
    try {
      const r = await axios.get(`${API}/subscription/status/${sessionId}`, { withCredentials: true });
      if (r.data?.payment_status === 'paid' || r.data?.active) {
        setSuccessMsg('🎉 Welcome to Premium — your trial is active!');
        await load();
        window.history.replaceState({}, '', '/settings');
        return;
      }
    } catch { /* keep polling */ }
    setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
  };

  const active = !!sub?.active;
  const onTrial = !!sub?.trial;
  const cancelling = !!sub?.cancel_at_period_end;
  const periodEnd = sub?.current_period_end ? new Date(sub.current_period_end) : null;
  const trialEnd = sub?.trial_ends_at ? new Date(sub.trial_ends_at) : periodEnd;
  const target = onTrial ? trialEnd : periodEnd;
  const daysLeft = target ? Math.max(0, Math.ceil((target.getTime() - Date.now()) / 86400000)) : null;
  const fmtDate = (d) => d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const amount = sub?.next_amount;
  const interval = sub?.next_interval;
  const priceLabel = `${amount != null ? '$' + amount : ''}${interval ? '/' + interval : ''}`.trim();

  const doCancel = async () => {
    setBusy(true); setErr('');
    try { await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true }); await load(); setConfirmOpen(false); }
    catch (e) { setErr(e.response?.data?.detail || "Couldn't cancel. Please try again."); }
    finally { setBusy(false); }
  };
  const doResume = async () => {
    setBusy(true); setErr('');
    try { await axios.post(`${API}/subscription/resume`, {}, { withCredentials: true }); await load(); }
    catch (e) { setErr(e.response?.data?.detail || "Couldn't resume. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="subscription-block">
      <div className="flex items-center gap-3 mb-1">
        <Crown className="w-5 h-5 text-[#C9A85B]" />
        <h3 className="text-xl font-semibold text-[#2D241E]" style={{ fontFamily: 'Outfit, sans-serif' }}>Homegrown Premium</h3>
      </div>

      {successMsg && (
        <div className="mt-3 flex items-center gap-2 bg-[#B4D330]/15 border border-[#6B9F4A]/40 rounded-xl px-4 py-2.5" data-testid="subscription-success">
          <CheckCircle2 className="w-4 h-4 text-[#3E6B2E]" />
          <span className="text-sm text-[#1A4331]">{successMsg}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-6 h-6 text-[#1A4331] animate-spin" /></div>
      ) : !active ? (
        <>
          <p className="text-sm text-[#5C665C] mt-1">You&apos;re on the Free plan. Unlock unlimited AI advice, every tool and the full community.</p>
          <button onClick={() => navigate('/paywall')} data-testid="settings-upgrade-premium"
            className="hg-press mt-4 inline-flex items-center gap-2 bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold px-6 py-3 rounded-full transition-colors">
            <Sprout className="w-4 h-4" style={{ color: '#B4D330' }} /> Upgrade to Premium
          </button>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 mt-1">
            <p className="text-sm text-[#5C665C]">{onTrial ? 'Free trial active' : 'Active subscription'}</p>
            {onTrial && daysLeft != null && (
              <span className="inline-flex items-center gap-1.5 bg-[#B4D330]/20 text-[#1A4331] text-xs font-bold px-3 py-1.5 rounded-full"
                data-testid="trial-days-chip" style={{ boxShadow: '0 0 12px -2px rgba(107,159,74,0.7)' }}>
                <span className="w-2 h-2 rounded-full" style={{ background: '#6B9F4A' }} /> {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left
              </span>
            )}
          </div>

          <div className={`mt-4 flex items-center gap-2 rounded-xl px-4 py-2.5 ${cancelling ? 'bg-[#C66B4A]/10' : 'bg-[#1A4331]/5'}`} data-testid="subscription-detail">
            {cancelling ? <AlertTriangle className="w-4 h-4 text-[#C66B4A]" /> : <Calendar className="w-4 h-4 text-[#1A4331]" />}
            <span className={`text-sm ${cancelling ? 'text-[#C66B4A]' : 'text-[#1A4331]'}`}>
              {cancelling
                ? `Premium ends ${fmtDate(target)} — won't renew`
                : onTrial
                  ? `Then ${priceLabel} from ${fmtDate(trialEnd)}`
                  : `Renews ${fmtDate(periodEnd)}${priceLabel ? ' · ' + priceLabel : ''}`}
            </span>
          </div>

          {err && <p className="text-sm text-[#C66B4A] mt-3" data-testid="subscription-error">{err}</p>}

          {cancelling ? (
            <button onClick={doResume} disabled={busy} data-testid="subscription-resume"
              className="hg-press mt-4 inline-flex items-center gap-2 bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold px-6 py-3 rounded-full transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Resume subscription
            </button>
          ) : (
            <button onClick={() => { setErr(''); setConfirmOpen(true); }} data-testid="subscription-cancel-open"
              className="hg-press mt-4 inline-flex items-center gap-2 bg-[#C66B4A]/12 hover:bg-[#C66B4A]/20 text-[#C66B4A] font-semibold px-6 py-3 rounded-full transition-colors">
              <XCircle className="w-4 h-4" /> Cancel subscription
            </button>
          )}
        </>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="cancel-subscription-modal">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md">
            <div className="w-11 h-11 rounded-full bg-[#C66B4A]/12 flex items-center justify-center mb-3"><XCircle className="w-5 h-5 text-[#C66B4A]" /></div>
            <h3 className="text-xl font-semibold text-[#2D241E]" style={{ fontFamily: 'Outfit, sans-serif' }}>Cancel Premium?</h3>
            <p className="text-sm text-[#5C665C] mt-2 mb-5">
              You&apos;ll keep every Premium feature until {fmtDate(target)}. After that your garden reverts to the Free plan. You can resume anytime before then.
            </p>
            {err && <p className="text-sm text-[#C66B4A] mb-3" data-testid="cancel-modal-error">{err}</p>}
            <div className="flex gap-3">
              <button onClick={() => setConfirmOpen(false)} disabled={busy} data-testid="keep-premium-button"
                className="flex-1 bg-[#F0F2EB] text-[#1A4331] font-semibold py-3 rounded-full">Keep Premium</button>
              <button onClick={doCancel} disabled={busy} data-testid="confirm-cancel-button"
                className="flex-1 bg-[#C66B4A] text-white font-semibold py-3 rounded-full disabled:opacity-50 flex items-center justify-center">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LegalSupportSection() {
  const { t } = useTranslation();
  return (
    <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="legal-support-section">
      <div className="flex items-center gap-3 mb-4">
        <FileText className="w-5 h-5 text-[#1A4331]" />
        <h3 className="text-xl font-semibold text-[#2D241E]" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('settings.legalSupport')}</h3>
      </div>
      <div className="divide-y divide-[#1A4331]/5">
        <a href="/terms" data-testid="open-terms" className="flex items-center gap-3 py-3 group">
          <div className="w-10 h-10 rounded-full bg-[#1A4331]/8 flex items-center justify-center"><FileText className="w-[18px] h-[18px] text-[#1A4331]" /></div>
          <span className="flex-1 font-medium text-[#2D241E]">{t('settings.termsOfUse')}</span>
          <ChevronRight className="w-5 h-5 text-[#7A6B57]" />
        </a>
        <a href="/privacy" data-testid="open-privacy" className="flex items-center gap-3 py-3 group">
          <div className="w-10 h-10 rounded-full bg-[#1A4331]/8 flex items-center justify-center"><Shield className="w-[18px] h-[18px] text-[#1A4331]" /></div>
          <span className="flex-1 font-medium text-[#2D241E]">{t('settings.privacyPolicy')}</span>
          <ChevronRight className="w-5 h-5 text-[#7A6B57]" />
        </a>
        <a href={`mailto:${SUPPORT_EMAIL}?subject=Homegrown support`} data-testid="contact-support" className="flex items-center gap-3 py-3 group">
          <div className="w-10 h-10 rounded-full bg-[#1A4331]/8 flex items-center justify-center"><Mail className="w-[18px] h-[18px] text-[#1A4331]" /></div>
          <span className="flex-1"><span className="font-medium text-[#2D241E] block">{t('settings.contactSupport')}</span><span className="text-sm text-[#5C665C]">{SUPPORT_EMAIL}</span></span>
          <ChevronRight className="w-5 h-5 text-[#7A6B57]" />
        </a>
      </div>
    </div>
  );
}

function DeleteAccountSection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const deleteAccount = async () => {
    setDeleting(true);
    setError('');
    try {
      await axios.delete(`${API}/users/me`, { data: { confirmation: 'DELETE' }, withCredentials: true });
      window.location.href = '/';
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not delete your account. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl p-6 border border-[#C66B4A]/25 shadow-sm" data-testid="danger-zone-section">
      <div className="flex items-center gap-3 mb-1">
        <AlertTriangle className="w-5 h-5 text-[#C66B4A]" />
        <h3 className="text-xl font-semibold text-[#C66B4A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('settings.deleteAccount')}</h3>
      </div>
      <p className="text-sm text-[#5C665C] mb-5">
        {t('settings.deleteAccountDesc')}
      </p>
      <button
        onClick={() => { setOpen(true); setConfirmText(''); setError(''); }}
        data-testid="open-delete-account-button"
        className="inline-flex items-center gap-2 bg-[#C66B4A]/10 hover:bg-[#C66B4A]/20 text-[#C66B4A] font-semibold py-2.5 px-5 rounded-full transition-colors"
      >
        <Trash2 className="w-4 h-4" /> {t('settings.deleteMyAccount')}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="delete-account-modal">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-full bg-[#C66B4A]/12 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-[#C66B4A]" />
              </div>
              <h4 className="text-lg font-semibold text-[#2D241E]" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('settings.deleteConfirmTitle')}</h4>
            </div>
            <p className="text-sm text-[#5C665C] mb-4 leading-relaxed">
              This will permanently erase your account and everything in it. There is no way to recover it. Type <span className="font-bold text-[#C66B4A]">DELETE</span> below to confirm.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={t('settings.deletePlaceholder')}
              data-testid="delete-confirm-input"
              className="w-full px-4 py-3 bg-[#F0F2EB] border-transparent focus:border-[#C66B4A] focus:ring-2 focus:ring-[#C66B4A]/20 rounded-xl outline-none transition-all mb-3"
            />
            {error && <p className="text-sm text-[#C66B4A] mb-3" data-testid="delete-error">{error}</p>}
            <div className="flex gap-3">
              <button
                onClick={() => setOpen(false)}
                disabled={deleting}
                data-testid="cancel-delete-button"
                className="flex-1 bg-[#F0F2EB] hover:bg-[#E5EBDD] text-[#2D241E] font-semibold py-3 px-5 rounded-full transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={deleteAccount}
                disabled={confirmText.trim().toUpperCase() !== 'DELETE' || deleting}
                data-testid="confirm-delete-button"
                className="flex-1 bg-[#C66B4A] hover:bg-[#B15A3B] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 px-5 rounded-full transition-colors"
              >
                {deleting ? `${t('settings.saving')}` : t('settings.deleteForever')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FeatureToggle({ icon: Icon, title, desc, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-11 h-11 rounded-full bg-[#1A4331]/8 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-[#1A4331]" />
      </div>
      <div className="flex-1">
        <p className="font-semibold text-[#2D241E]">{title}</p>
        <p className="text-sm text-[#5C665C]">{desc}</p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        data-testid={testId}
        className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-[#6B9F4A]' : 'bg-[#D9E1CB]'}`}
      >
        <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${checked ? 'left-[22px]' : 'left-0.5'}`}></span>
      </button>
    </div>
  );
}

function PrivacySection() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/profiles/me`, { withCredentials: true })
      .then((r) => { setProfile(r.data); setDisplayName(r.data.display_name || ''); setBio(r.data.bio || ''); })
      .catch(() => {});
  }, []);

  const patch = async (body) => {
    setSaving(true); setSaved(false);
    try {
      const r = await axios.patch(`${API}/profiles/me`, body, { withCredentials: true });
      setProfile(r.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { /* noop */ } finally { setSaving(false); }
  };

  const togglePublic = (v) => { setProfile((p) => ({ ...p, is_public: v })); patch({ is_public: v }); };

  if (!profile) return null;
  const isPublic = !!profile.is_public;

  return (
    <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="privacy-section">
      <div className="flex items-center gap-3 mb-1">
        <Users className="w-5 h-5 text-[#1A4331]" />
        <h3 className="text-xl font-semibold text-[#2D241E]" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('settings.communityPrivacy')}</h3>
      </div>
      <p className="text-sm text-[#5C665C] mb-5">{t('settings.communityPrivacyDesc')}</p>

      <div className="flex items-center gap-4 mb-5">
        <div className="w-11 h-11 rounded-full bg-[#1A4331]/8 flex items-center justify-center flex-shrink-0">
          {isPublic ? <Eye className="w-5 h-5 text-[#6B9F4A]" /> : <EyeOff className="w-5 h-5 text-[#7A6B57]" />}
        </div>
        <div className="flex-1">
          <p className="font-semibold text-[#2D241E]">{isPublic ? t('settings.publicGrower') : t('settings.privateGrower')}</p>
          <p className="text-sm text-[#5C665C]">{isPublic ? t('settings.publicGrowerDesc') : t('settings.privateGrowerDesc')}</p>
        </div>
        <button
          role="switch"
          aria-checked={isPublic}
          disabled={saving}
          onClick={() => togglePublic(!isPublic)}
          data-testid="toggle-public-profile"
          className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${isPublic ? 'bg-[#6B9F4A]' : 'bg-[#D9E1CB]'}`}
        >
          <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${isPublic ? 'left-[22px]' : 'left-0.5'}`}></span>
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-[#2D241E] mb-2">{t('settings.displayName')}</label>
          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={40} data-testid="display-name-input"
            className="w-full px-4 py-3 bg-[#F0F2EB] border-transparent focus:border-[#8EAA97] focus:ring-2 focus:ring-[#8EAA97]/20 rounded-xl outline-none transition-all" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-[#2D241E]">{t('settings.shortBio')}</label>
            <EmojiPicker testId="bio-emoji" onSelect={(e) => setBio((b) => (b + e).slice(0, 400))} />
          </div>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} maxLength={400} placeholder={t('settings.bioPlaceholder')} data-testid="bio-input"
            className="w-full px-4 py-3 bg-[#F0F2EB] border-transparent focus:border-[#8EAA97] focus:ring-2 focus:ring-[#8EAA97]/20 rounded-xl outline-none transition-all" />
        </div>
        <button onClick={() => patch({ display_name: displayName, bio })} disabled={saving} data-testid="save-profile-button"
          className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-2.5 px-6 rounded-full transition-colors disabled:opacity-50">
          {saving ? `${t('settings.saving')}` : saved ? `${t('common.saved')} ✓` : t('settings.saveProfile')}
        </button>
      </div>
    </div>
  );
}

function LocationCard({ user, onSave }) {
  const { t } = useTranslation();
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [locText, setLocText] = useState(user?.location || '');
  const [region, setRegion] = useState(user?.region || '');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [gps, setGps] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API_BASE}/geo/search`, { params: { q: query }, withCredentials: true });
        setResults(r.data?.results || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [query, API_BASE]);

  const persist = async (patch, note) => {
    setSaving(true); setMsg('');
    try { await onSave(patch); setMsg(note || 'Location saved ✓'); }
    catch { setMsg('Could not save — please try again.'); }
    finally { setSaving(false); }
  };

  const pick = (r) => {
    setLocText(r.label); setQuery(''); setResults([]);
    persist({ latitude: r.latitude, longitude: r.longitude, location: r.label, country: r.country || user?.country || null }, `Set to ${r.label} ✓`);
  };

  const useGps = () => {
    if (!('geolocation' in navigator)) { setMsg('Your browser does not support location.'); return; }
    setGps(true); setMsg('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const label = `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`;
        setLocText(label); setGps(false);
        persist({ latitude, longitude, location: label }, 'Using your current location ✓');
      },
      (err) => { setGps(false); setMsg(err.code === 1 ? 'Location permission denied — search for your town below instead.' : 'Could not get your location — try searching below.'); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
    );
  };

  const inputCls = 'w-full px-4 py-3 bg-[#F0F2EB] border-transparent focus:border-[#8EAA97] focus:ring-2 focus:ring-[#8EAA97]/20 rounded-xl outline-none transition-all';

  return (
    <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="location-section">
      <div className="flex items-center gap-3 mb-2">
        <MapPin className="w-5 h-5 text-[#1A4331]" />
        <h3 className="text-xl font-semibold text-[#2D241E]" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('settings.locationClimate')}</h3>
      </div>
      <p className="text-sm text-[#5C665C] mb-4">
        We use this for accurate weather, frost warnings and seasonal advice. Current: <span className="font-semibold text-[#1A4331]" data-testid="location-current">{user?.location || 'not set'}</span>
      </p>

      <button onClick={useGps} disabled={gps || saving} data-testid="location-use-gps"
        className="w-full flex items-center justify-center gap-2 px-4 py-3 mb-4 rounded-full bg-[#1A4331] text-white font-semibold disabled:opacity-60 transition-all hover:opacity-90">
        {gps ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
        {gps ? `${t('settings.detecting')}` : t('settings.useCurrentLocation')}
      </button>

      <label className="block text-sm font-semibold text-[#2D241E] mb-2">{t('settings.searchPlace')}</label>
      <div className="relative mb-1">
        <Search className="w-4 h-4 text-[#8EAA97] absolute left-4 top-1/2 -translate-y-1/2" />
        <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. Auckland, Sydney, Dunedin"
          data-testid="location-search-input" className={inputCls + ' pl-10'} />
        {searching ? <Loader2 className="w-4 h-4 text-[#8EAA97] absolute right-4 top-1/2 -translate-y-1/2 animate-spin" /> : null}
      </div>
      {results.length > 0 ? (
        <div className="border border-[#1A4331]/10 rounded-xl overflow-hidden mb-4" data-testid="location-results">
          {results.map((r, i) => (
            <button key={`${r.latitude},${r.longitude}`} onClick={() => pick(r)} data-testid={`location-result-${i}`}
              className="w-full text-left px-4 py-3 hover:bg-[#F0F2EB] border-b border-[#1A4331]/5 last:border-0 transition-colors">
              <span className="text-[#2D241E] font-medium">{r.label}</span>
            </button>
          ))}
        </div>
      ) : <div className="mb-4" />}

      <label className="block text-sm font-semibold text-[#2D241E] mb-2">{t('settings.editManually')}</label>
      <input type="text" value={locText} onChange={(e) => setLocText(e.target.value)} placeholder="e.g., Auckland, New Zealand"
        data-testid="location-input" className={inputCls + ' mb-3'} />
      <label className="block text-sm font-semibold text-[#2D241E] mb-2">Your region</label>
      <RegionPicker value={region} onChange={(id) => setRegion(id)} testId="settings-region-select" className={inputCls + ' mb-1'} />
      <div className="mb-4" />

      <button onClick={() => persist({ location: locText, region }, 'Location saved ✓')} disabled={saving}
        data-testid="location-save-button"
        className="w-full px-4 py-3 rounded-full bg-[#C9A85B] text-[#1A4331] font-semibold disabled:opacity-60 transition-all hover:opacity-90">
        {saving ? `${t('settings.saving')}` : t('settings.saveLocation')}
      </button>
      {msg ? <p className="text-sm text-[#4A7C4E] mt-3 font-medium" data-testid="location-status">{msg}</p> : null}
    </div>
  );
}

export default SettingsPage;
